import React, { useState, useEffect } from 'react';
import './Admin.css';

interface Game {
  id: number;
  title: string;
  description: string;
  thumbnail_url: string;
  game_url: string;
  category_id: number;
  category_name: string;
  rating: number;
  ratingCount: number;
  seoDesc: string;
  howtoplay: string;
  appId: string;
  play_count: number;
  isfeatured: boolean;
  layout?: string;
  urlkey?: string;
  filesCount?: number;
  filesSize?: number;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

const Admin: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingGame, setEditingGame] = useState<Game | null>(null);
  const [newGame, setNewGame] = useState({
    title: '',
    description: '',
    seoDesc: '',
    howtoplay: '',
    rating: 0,
    ratingCount: 0,
    appId: '',
    thumbnail_url: '',
    game_url: '',
    category_id: 1,
    play_count: 0,
    isfeatured: false,
    layout: 'auto',
    urlkey: '',
    filesCount: 0,
    filesSize: 0,
  });
  const [newCategory, setNewCategory] = useState({
    name: '',
    slug: ''
  });
  const [sortByFeatured, setSortByFeatured] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const gamesPerPage = 50;
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchGames();
    fetchCategories();
  }, [sortByFeatured, currentPage, searchTerm]);

  const fetchGames = async () => {
    try {
      const response = await fetch(`/api/admin/games?sortByFeatured=${sortByFeatured}&page=${currentPage}&limit=${gamesPerPage}&search=${encodeURIComponent(searchTerm)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setGames(data.games);
      setTotalPages(Math.ceil(data.total / gamesPerPage));
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/admin/categories');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddGame = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/admin/games', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newGame),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
        setNewGame({
          title: '',
          description: '',
          seoDesc: '',
          howtoplay: '',
          rating: 0,
          ratingCount: 0,
          appId: '',
          thumbnail_url: '',
          game_url: '',
          category_id: 1,
          play_count: 0,
          isfeatured: false,
          layout: 'auto',
          urlkey: '',
          filesCount: 0,
          filesSize: 0,
        });
      await fetchGames();
    } catch (error) {
      console.error('Error adding game:', error);
    }
  };

  const handleUpdateGame = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGame) return;

    try {
      const response = await fetch(`/api/admin/games/${editingGame.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingGame),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
        setEditingGame(null);
      await fetchGames();
    } catch (error) {
      console.error('Error updating game:', error);
    }
  };

  const handleDeleteGame = async (id: number) => {
    try {
      const response = await fetch(`/api/admin/games/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await fetchGames();
    } catch (error) {
      console.error('Error deleting game:', error);
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`/api/categories/${categoryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Refresh categories list
        fetchCategories();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const handleToggleFeatured = async (gameId: number, currentFeatured: boolean) => {
    try {
      const response = await fetch(`/api/admin/games/${gameId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isfeatured: !currentFeatured })
      });
      if (response.ok) {
        fetchGames();
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/admin/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newCategory),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setNewCategory({
        name: '',
        slug: ''
      });
      await fetchCategories();
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  return (
    <div className="admin-container">
      <h1>Admin Panel</h1>
      
      {/* Categories Section */}
      <section className="admin-section">
        <h2>Categories</h2>
        
        {/* Add Category Form */}
        <div className="add-category-form">
          <h3>Add New Category</h3>
          <form onSubmit={handleAddCategory}>
            <div className="form-group">
              <label>Name:</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={e => setNewCategory({...newCategory, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Slug:</label>
              <input
                type="text"
                value={newCategory.slug}
                onChange={e => setNewCategory({...newCategory, slug: e.target.value})}
                required
                placeholder="e.g., action-games"
              />
            </div>
            <button type="submit">Add Category</button>
          </form>
        </div>

        <div className="categories-list">
          {categories.map(category => (
            <div key={category.id} className="category-item">
              <span>
                {category.name} <span style={{color:'#888', fontSize:'0.95em'}}> (ID: {category.id})</span>
              </span>
              <button 
                onClick={() => handleDeleteCategory(category.id)}
                className="delete-button"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Games Section */}
      <section className="admin-section">
        <h2>Games</h2>
        <section className="create-game">
          <h2>Create New Game</h2>
          <form onSubmit={handleAddGame}>
            <div className="form-group">
              <label>Title:</label>
              <input
                type="text"
                value={newGame.title}
                onChange={e => setNewGame({...newGame, title: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={newGame.description}
                onChange={e => setNewGame({...newGame, description: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>SEO Description:</label>
              <input
                type="text"
                value={newGame.seoDesc}
                onChange={e => setNewGame({...newGame, seoDesc: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>How to Play:</label>
              <textarea
                value={newGame.howtoplay}
                onChange={e => setNewGame({...newGame, howtoplay: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Rating Count:</label>
              <input
                type="number"
                value={newGame.ratingCount}
                onChange={e => setNewGame({...newGame, ratingCount: Number(e.target.value)})}
              />
            </div>
            <div className="form-group">
              <label>App ID:</label>
              <input
                type="text"
                value={newGame.appId}
                onChange={e => setNewGame({...newGame, appId: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Thumbnail URL:</label>
              <input
                type="text"
                value={newGame.thumbnail_url}
                onChange={e => setNewGame({...newGame, thumbnail_url: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Game URL:</label>
              <input
                type="text"
                value={newGame.game_url}
                onChange={e => setNewGame({...newGame, game_url: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Category:</label>
              <select
                value={newGame.category_id}
                onChange={e => setNewGame({...newGame, category_id: Number(e.target.value)})}
                required
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Rating:</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={newGame.rating}
                onChange={e => setNewGame({...newGame, rating: Number(e.target.value)})}
                required
              />
            </div>
            <div className="form-group">
              <label>Play Count:</label>
              <input
                type="number"
                min="0"
                value={newGame.play_count}
                onChange={e => setNewGame({...newGame, play_count: Number(e.target.value)})}
                required
              />
            </div>
            <div className="form-group">
              <label>Layout:</label>
              <select
                value={newGame.layout}
                onChange={e => setNewGame({...newGame, layout: e.target.value})}
                required
              >
                <option value="auto">auto</option>
                <option value="landscape">landscape</option>
                <option value="portrait">portrait</option>
              </select>
            </div>
            <div className="form-group">
              <label>URL Key:</label>
              <input
                type="text"
                value={newGame.urlkey || ''}
                onChange={e => setNewGame({...newGame, urlkey: e.target.value})}
                required
                placeholder="如：my-game-title"
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={newGame.isfeatured}
                  onChange={e => setNewGame({...newGame, isfeatured: e.target.checked})}
                />
                {' '}Featured (isfeatured)
              </label>
            </div>
            <div className="form-group">
              <label>Files Count:</label>
              <input
                type="number"
                value={newGame.filesCount}
                onChange={e => setNewGame({...newGame, filesCount: Number(e.target.value)})}
              />
            </div>
            <div className="form-group">
              <label>Files Size (MB):</label>
              <input
                type="number"
                value={newGame.filesSize}
                onChange={e => setNewGame({...newGame, filesSize: Number(e.target.value)})}
              />
            </div>
            <button type="submit">Create Game</button>
          </form>
        </section>
        {/* 搜索框 */}
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search by game name..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={{ padding: '0.5rem', width: '300px', fontSize: '1rem' }}
          />
        </div>
        <section className="games-list">
          <div className="games-header">
            <h2>Games List</h2>
            <button 
              className={`sort-button ${sortByFeatured ? 'active' : ''}`}
              onClick={() => setSortByFeatured(!sortByFeatured)}
            >
              {sortByFeatured ? '★ Featured First' : '☆ All Games'}
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Category</th>
                <th>App ID</th>
                <th>Featured</th>
                <th>Layout</th>
                <th>链接</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {games.map(game => (
                <tr key={game.id}>
                  <td>{game.id}</td>
                  <td>{game.title}</td>
                  <td>{game.category_name}</td>
                  <td>{game.appId}</td>
                  <td>
                    <button 
                      onClick={() => handleToggleFeatured(game.id, game.isfeatured)}
                      className={game.isfeatured ? 'featured-button active' : 'featured-button'}
                    >
                      {game.isfeatured ? '★' : '☆'}
                    </button>
                  </td>
                  <td>{game.layout}</td>
                  <td>
                    <a href={`/game/${game.urlkey}`} target="_blank" rel="noopener noreferrer">查看</a>
                  </td>
                  <td>
                    <button onClick={() => setEditingGame(game)}>Edit</button>
                    <button onClick={() => handleDeleteGame(game.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </section>

        {editingGame && (
          <div className="edit-modal">
            <div className="modal-content">
              <h2>Edit Game</h2>
              <form onSubmit={handleUpdateGame}>
                <div className="form-group">
                  <label>Title:</label>
                  <input
                    type="text"
                    value={editingGame.title ?? ''}
                    onChange={e => setEditingGame({...editingGame, title: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description:</label>
                  <textarea
                    value={editingGame.description ?? ''}
                    onChange={e => setEditingGame({...editingGame, description: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>SEO Description:</label>
                  <input
                    type="text"
                    value={editingGame.seoDesc ?? ''}
                    onChange={e => setEditingGame({...editingGame, seoDesc: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>How to Play:</label>
                  <textarea
                    value={editingGame.howtoplay ?? ''}
                    onChange={e => setEditingGame({...editingGame, howtoplay: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Rating Count:</label>
                  <input
                    type="number"
                    value={editingGame.ratingCount ?? 0}
                    onChange={e => setEditingGame({...editingGame, ratingCount: Number(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label>App ID:</label>
                  <input
                    type="text"
                    value={editingGame.appId ?? ''}
                    onChange={e => setEditingGame({...editingGame, appId: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Thumbnail URL:</label>
                  <input
                    type="text"
                    value={editingGame.thumbnail_url ?? ''}
                    onChange={e => setEditingGame({...editingGame, thumbnail_url: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Game URL:</label>
                  <input
                    type="text"
                    value={editingGame.game_url ?? ''}
                    onChange={e => setEditingGame({...editingGame, game_url: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category:</label>
                  <select
                    value={editingGame.category_id ?? ''}
                    onChange={e => setEditingGame({...editingGame, category_id: Number(e.target.value)})}
                    required
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Rating:</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={editingGame.rating ?? 0}
                    onChange={e => setEditingGame({...editingGame, rating: Number(e.target.value)})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Play Count:</label>
                  <input
                    type="number"
                    min="0"
                    value={editingGame.play_count ?? 0}
                    onChange={e => setEditingGame({...editingGame, play_count: Number(e.target.value)})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Layout:</label>
                  <select
                    value={editingGame.layout}
                    onChange={e => setEditingGame({...editingGame, layout: e.target.value})}
                    required
                  >
                    <option value="auto">auto</option>
                    <option value="landscape">landscape</option>
                    <option value="portrait">portrait</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>URL Key:</label>
                  <input
                    type="text"
                    value={editingGame.urlkey ?? ''}
                    onChange={e => setEditingGame({...editingGame, urlkey: e.target.value})}
                    required
                    placeholder="如：my-game-title"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!editingGame.isfeatured}
                      onChange={e => setEditingGame({...editingGame, isfeatured: e.target.checked})}
                    />
                    {' '}Featured (isfeatured)
                  </label>
                </div>
                <div className="form-group">
                  <label>Files Count:</label>
                  <input
                    type="number"
                    value={editingGame.filesCount ?? 0}
                    onChange={e => setEditingGame({...editingGame, filesCount: Number(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label>Files Size (MB):</label>
                  <input
                    type="number"
                    value={editingGame.filesSize ?? 0}
                    onChange={e => setEditingGame({...editingGame, filesSize: Number(e.target.value)})}
                  />
                </div>
                <div className="button-group">
                  <button type="submit">Save Changes</button>
                  <button type="button" onClick={() => setEditingGame(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Admin; 